import type { FC, ReactNode } from 'react'
import React, { useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'

export interface SlideInSidebarProps {
  onClose: () => void
  header?: ReactNode
  content?: ReactNode
  footer?: ReactNode
}

const slideInTop = keyframes`
  0% {
    -webkit-transform: translateX(800px);
    transform: translateX(800px);
  }
  100% {
    -webkit-transform: translateX(0);
    transform: translateX(0);
  }
`

const Container = styled.div`
  background-color: #ffffff;
  position: fixed;
  width: 40%;
  top: 0;
  right: 0;
  bottom: 0px;
  animation: ${slideInTop} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 10000;
  padding: 25px;
  border-left: #f2f2f2 solid 2px;
  display: grid;
  grid-template-rows: min-content 1fr min-content;
`

const HeaderWrapper = styled.div`
  grid-row-start: 1;
`

const ContentWrapper = styled.div`
  grid-row-start: 2;
  overflow-y: auto;
`

const FooterWrapper = styled.div`
  grid-row-start: 3;
`

const SlideInSidebar: FC<SlideInSidebarProps> = ({
  header,
  content,
  footer,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose()
      }
    }

    const handleKeyPress = (event: any) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyPress)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [ref, onClose])

  return (
    <Container ref={ref}>
      {header && <HeaderWrapper>{header}</HeaderWrapper>}
      <ContentWrapper>{content}</ContentWrapper>
      {footer && <FooterWrapper>{footer}</FooterWrapper>}
    </Container>
  )
}

SlideInSidebar.displayName = 'Slide In Sidebar'

export default SlideInSidebar
